import {
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  listItemIconClasses,
  listItemTextClasses,
} from '@mui/material';
import type { ListItemButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  selectIsOrganizationDataAccessible,
  selectViewsRestrictions,
  ViewAccessType,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import { useAppSelector } from '@/shared/model';
import {
  CalendarChartIcon,
  CalendarInsightsIcon,
  CohortChartIcon,
  CohortMarketingPerformanceIcon,
  CohortRetentionIcon,
  HomeIcon,
  ROIBreakdownIcon,
  CohortScannerIcon,
  ProductsCatalogIcon,
  DatasourcesIcon,
  DashboardsIcon,
  OverviewIcon,
} from '@/shared/ui';

const List = styled('ul')<{ expanded: boolean }>(({ theme, expanded }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  overflowX: 'hidden',
  overflowY: 'auto',
  scrollbarWidth: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  ...(expanded && {
    scrollbarWidth: 'thin',
  }),
}));
type ItemProps = ListItemButtonProps & {
  to: string;
  expanded: boolean;
  end?: boolean;
};
const Item = styled(({ to, expanded, ...props }: ItemProps) => {
  const handleClick = () => {
    Analytics.sendFeatureUsage('view_navigation', 'navigation_drawer_item', {
      view: to,
    });
  };

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      onClick={handleClick}
      {...props}
    />
  );
})<ItemProps>(({ theme, expanded }) => ({
  padding: theme.spacing(1),
  height: 40,
  display: 'flex',
  gap: theme.spacing(1.25),
  fontWeight: 500,
  fontSize: '0.875rem',
  borderRadius: theme.shape.borderRadius,

  [`& .${listItemIconClasses.root}`]: {
    minWidth: 0,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${listItemTextClasses.root}`]: {
    opacity: 0,
    visibility: 'hidden',
  },

  ...(expanded && {
    [`& .${listItemTextClasses.root}`]: {
      opacity: 1,
      visibility: 'visible',
      transition: 'opacity 0.1s ease-in-out 0.2s',
    },
  }),

  '&.active': {
    backgroundColor: '#ECF0FE',

    [`& .${listItemIconClasses.root}`]: {
      color: '#063FFE',
    },
  },
}));

interface Props {
  expanded: boolean;
}

export function NavigationList({ expanded }: Props) {
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const isOrganizationDataAccessible = useAppSelector(
    selectIsOrganizationDataAccessible,
  );

  useEffect(() => {
    document.querySelector('a.active')?.scrollIntoView({ behavior: 'instant' });
  }, []);

  return (
    <List expanded={expanded}>
      <Item expanded={expanded} end to={getRedesignRoute(ROUTES.home)}>
        <ListItemIcon>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText disableTypography primary="Home" />
      </Item>
      {isOrganizationDataAccessible &&
        viewsRestrictions?.cohortMarketingPerformance !==
          ViewAccessType.DENIED && (
          <Item
            expanded={expanded}
            to={getRedesignRoute(ROUTES.cohortMarketingPerformance)}
          >
            <ListItemIcon>
              <CohortMarketingPerformanceIcon />
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary="Cohort Marketing Performance"
            />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.calendarInsights !== ViewAccessType.DENIED && (
          <Item
            expanded={expanded}
            to={getRedesignRoute(ROUTES.calendarInsights)}
          >
            <ListItemIcon>
              <CalendarInsightsIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Calendar Insights" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.cohortChart !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.cohortChart)}>
            <ListItemIcon>
              <CohortChartIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Cohort Chart" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.calendarChart !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.calendarChart)}>
            <ListItemIcon>
              <CalendarChartIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Calendar Chart" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.cohortScanner !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.cohortScanner)}>
            <ListItemIcon>
              <CohortScannerIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Cohort Scanner" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.cohortRetention !== ViewAccessType.DENIED && (
          <Item
            expanded={expanded}
            to={getRedesignRoute(ROUTES.cohortRetention)}
          >
            <ListItemIcon>
              <CohortRetentionIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Cohort Retention" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.roiBreakdown !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.roiBreakdown)}>
            <ListItemIcon>
              <ROIBreakdownIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="ROI Breakdown" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.dashboards !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.dashboards)}>
            <ListItemIcon>
              <DashboardsIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Dashboards" />
          </Item>
        )}
      {isOrganizationDataAccessible &&
        viewsRestrictions?.overview !== ViewAccessType.DENIED && (
          <Item expanded={expanded} to={getRedesignRoute(ROUTES.overview)}>
            <ListItemIcon>
              <OverviewIcon />
            </ListItemIcon>
            <ListItemText disableTypography primary="Overview" />
          </Item>
        )}
      <Divider key="administration-views-divider" />
      {viewsRestrictions?.productsCatalog !== ViewAccessType.DENIED && (
        <Item
          key="products-catalog"
          expanded={expanded}
          to={getRedesignRoute(ROUTES.productsCatalog)}
        >
          <ListItemIcon>
            <ProductsCatalogIcon />
          </ListItemIcon>
          <ListItemText disableTypography primary="Product Catalog" />
        </Item>
      )}
      {viewsRestrictions?.integrations !== ViewAccessType.DENIED && (
        <Item
          key="integrations"
          expanded={expanded}
          to={getRedesignRoute(ROUTES.datasources)}
        >
          <ListItemIcon>
            <DatasourcesIcon sx={{ color: '#666666' }} />
          </ListItemIcon>
          <ListItemText disableTypography primary="Integrations" />
        </Item>
      )}
    </List>
  );
}

const getRedesignRoute = (route: string): string => {
  return route;
};
